import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './useAuth';

interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amount_paid: number;
  amount_due: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  subscription_id: string;
  billing_reason: string | null;
  paid: boolean;
  attempted: boolean;
  next_payment_attempt: number | null;
  lines?: {
    description: string | null;
    amount: number;
    quantity: number | null;
    period: {
      start: number;
      end: number;
    };
  }[];
}

interface UseInvoicesResult {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useInvoices(limit: number = 10): UseInvoicesResult {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchInvoices = async (startingAfter?: string) => {
    try {
      setError(null);
      
      const body: any = { limit };
      if (startingAfter) {
        body.starting_after = startingAfter;
      }

      const { data, error: fetchError } = await supabase.functions.invoke('get-invoices', {
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Edge function response:', { data, error: fetchError });

      if (fetchError) {
        console.log('Edge function error details:', fetchError);
        // Handle "No billing account found" as a valid empty state
        if (fetchError.message?.includes('404') || fetchError.message?.includes('No billing account')) {
          setInvoices([]);
          setHasMore(false);
          return;
        }
        throw fetchError;
      }

      if (data) {
        if (startingAfter) {
          // Append to existing invoices
          setInvoices(prev => [...prev, ...data.invoices]);
        } else {
          // Replace invoices (initial load or refresh)
          setInvoices(data.invoices);
        }
        setHasMore(data.has_more);
        setNextCursor(data.next_cursor);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore || !nextCursor || loading) return;
    setLoading(true);
    await fetchInvoices(nextCursor);
  };

  const refresh = async () => {
    setLoading(true);
    setInvoices([]);
    setNextCursor(null);
    await fetchInvoices();
  };

  useEffect(() => {
    if (user) {
      fetchInvoices();
    } else {
      setInvoices([]);
      setLoading(false);
    }
  }, [user]);

  return {
    invoices,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  };
}

// Utility functions for formatting invoice data
export function formatInvoiceStatus(status: string | null): string {
  if (!status) return 'Unknown';
  
  const statusMap: Record<string, string> = {
    draft: 'Draft',
    open: 'Open',
    paid: 'Paid',
    uncollectible: 'Uncollectible',
    void: 'Void'
  };
  
  return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
}

export function formatCurrency(amount: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100); // Stripe amounts are in cents
}

export function formatInvoiceDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function getInvoiceStatusColor(status: string | null): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'open':
      return 'bg-yellow-100 text-yellow-800';
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'uncollectible':
    case 'void':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}